import { api, type ExecutiveResponse, type SerializedKpi } from '../lib/api';
import { Card } from '../components/Painel';
import { useResource } from '../lib/useResource';
import { formatNumber } from '../lib/dataState';
import { formatInstant } from '../lib/format';

/**
 * Painel executivo.
 *
 * É a tela que mais fica aberta em reunião e em compartilhamento de tela — e a
 * que mais precisa resistir a uma leitura apressada. Duas decisões de desenho:
 *
 * 1. **Nenhum estado que não seja medido renderiza um dígito.** Um "0" ao lado
 *    de "não medido" é lido como zero, por mais que o rótulo diga o contrário —
 *    o olho pega o número e ignora a legenda.
 *
 * 2. **O rótulo do estado vem antes do valor.** Ler "12.000" e depois descobrir
 *    que é dado de três dias atrás é diferente de ler "dado velho: 12.000".
 */

const KPI_LABEL: Record<string, string> = {
  reach_24h: 'Alcance (24h)',
  non_follower_reach_24h: 'Alcance de não-seguidores (24h)',
  paid_reach_24h: 'Alcance pago (24h)',
  combined_reach_24h: 'Alcance combinado (24h)',
  saves_24h: 'Salvamentos (24h)',
  follow_rate: 'Taxa de novos seguidores',
  lead_qualification_rate: 'Taxa de qualificação de leads',
  cost_per_meeting: 'Custo por reunião',
  published_count: 'Publicações na janela',
};

const KIND_CLASS: Record<string, string> = {
  measured: 'text-ink',
  estimated: 'text-accent',
  stale: 'text-warn-ink',
  unavailable: 'text-ink-faint',
  not_configured: 'text-ink-faint',
  insufficient_sample: 'text-ink-faint',
};

/** KPIs que são dinheiro em centavos: formatados à mão. */
const MONEY_KPIS = new Set(['cost_per_meeting']);
/** KPIs que são proporção: exibidos em percentual. */
const RATE_KPIS = new Set(['follow_rate', 'lead_qualification_rate']);

/**
 * Centavos para reais, escrito à mão.
 *
 * `Intl` varia com a versão do ICU e insere espaço não-quebrável — o mesmo valor
 * sairia diferente em máquinas diferentes, e duas telas do produto discordariam.
 */
export function formatCents(cents: number): string {
  const reais = Math.floor(Math.abs(cents) / 100);
  const resto = Math.abs(cents) % 100;
  const inteiro = String(reais).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const corpo = resto === 0 ? inteiro : `${inteiro},${String(resto).padStart(2, '0')}`;
  return `${cents < 0 ? '-' : ''}R$ ${corpo}`;
}

/** Proporção para percentual com uma casa. */
export function formatRate(ratio: number): string {
  const pct = Math.round(ratio * 1000) / 10;
  return `${String(pct).replace('.', ',')}%`;
}

/**
 * Renderiza o valor de um KPI.
 *
 * A função é a guarda visual do princípio: **só `measured` e `estimated`
 * produzem dígito**. Qualquer outro estado devolve o rótulo, e o número (quando
 * existe, como no caso do dado velho) aparece explicitamente subordinado a ele.
 */
export function renderKpi(id: string, kpi: SerializedKpi): string {
  if (kpi.kind === 'unavailable' || kpi.kind === 'not_configured') {
    return kpi.label;
  }
  if (kpi.kind === 'insufficient_sample') {
    return kpi.label;
  }
  if (kpi.value === null) {
    // Defesa: um estado que deveria ter valor e chegou sem ele não vira zero.
    return 'não medido';
  }

  const numero = MONEY_KPIS.has(id)
    ? formatCents(kpi.value)
    : RATE_KPIS.has(id)
      ? formatRate(kpi.value)
      : formatNumber(kpi.value);

  return kpi.kind === 'stale' ? `${kpi.label}: ${numero}` : numero;
}

export function Executive() {
  const resource = useResource<ExecutiveResponse>(() => api.executive(), []);

  if (resource.kind === 'loading') {
    return (
      <div className="text-sm" aria-busy="true">
        <h1 className="text-2xl font-bold">Painel executivo</h1>
        <p className="mt-2 text-ink-muted">Calculando…</p>
      </div>
    );
  }

  if (resource.kind === 'error') {
    return (
      <div className="max-w-3xl space-y-4 text-sm">
        <h1 className="text-2xl font-bold">Painel executivo</h1>
        <div className="mt-4 rounded border border-danger-border bg-danger-surface p-4">
          <p className="font-medium">Não foi possível montar o painel.</p>
          <p className="mt-1">{resource.message}</p>
          <p className="mt-2 text-xs text-ink-faint">
            Nenhum número aparece aqui: um painel que degrada para zeros faria você decidir sobre um
            desempenho que ninguém mediu. Código: {resource.code}
          </p>
        </div>
      </div>
    );
  }

  const { data } = resource;
  const entradas = Object.entries(data.kpis);

  return (
    <div className="space-y-4 text-sm">
      <header>
        <h1 className="text-2xl font-bold">Painel executivo</h1>
        <p className="mt-1 text-ink-muted">
          Janela de {formatNumber(data.windowDays)} dias · gerado em{' '}
          {formatInstant(data.generatedAt)} · dado é considerado velho após{' '}
          {formatNumber(Math.floor(data.freshnessSlaSeconds / 3600))}h.
        </p>
      </header>

      {data.anomalies.length > 0 ? (
        <section className="mt-4 rounded border border-warn-border bg-warn-surface/60 p-4">
          <Card>
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
              {formatNumber(data.anomalies.length)} anomalia(s) detectada(s)
            </h2>
            <ul className="mt-2 space-y-1 text-xs">
              {data.anomalies.map((a) => (
                <li key={a.kpi}>
                  <strong>{KPI_LABEL[a.kpi] ?? a.kpi}</strong> ({a.severity}): {a.reason}
                </li>
              ))}
            </ul>
          </Card>
        </section>
      ) : null}

      <section className="mt-6" aria-labelledby="indicadores">
        <Card>
          <h2
            id="indicadores"
            className="text-xs font-semibold uppercase tracking-wider text-ink-muted"
          >
            Indicadores
          </h2>
          <dl className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {entradas.map(([id, kpi]) => (
              <div key={id} className="rounded border border-line p-3">
                <dt className="text-xs text-ink-muted">{KPI_LABEL[id] ?? id}</dt>
                <dd className={`mt-1 text-base font-semibold ${KIND_CLASS[kpi.kind] ?? ''}`}>
                  {renderKpi(id, kpi)}
                </dd>
                {kpi.detail ? <p className="mt-1 text-xs text-ink-faint">{kpi.detail}</p> : null}
                {kpi.captured_at && kpi.kind === 'measured' ? (
                  <p className="mt-1 text-xs text-ink-faint">{formatInstant(kpi.captured_at)}</p>
                ) : null}
              </div>
            ))}
          </dl>
        </Card>
      </section>

      <section className="mt-8" aria-labelledby="agentes">
        <Card>
          <h2
            id="agentes"
            className="text-xs font-semibold uppercase tracking-wider text-ink-muted"
          >
            Desempenho por agente
          </h2>
          {data.agentPerformance.length === 0 ? (
            <p className="mt-3 rounded-[11px] border border-line bg-surface-sunken p-4 text-ink-muted">
              Nenhuma decisão registrada na janela. Isso não é desempenho ruim: é ausência de
              atividade.
            </p>
          ) : (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr className="border-b border-line text-left text-ink-muted">
                    <th className="py-2 pr-4 font-medium">Agente</th>
                    <th className="py-2 pr-4 font-medium">Decisões</th>
                    <th className="py-2 pr-4 font-medium">Taxa de bloqueio</th>
                    <th className="py-2 font-medium">Taxa de override</th>
                  </tr>
                </thead>
                <tbody>
                  {data.agentPerformance.map((a) => (
                    <tr key={a.agent} className="border-b border-line">
                      <td className="py-2 pr-4">{a.agent}</td>
                      <td className="py-2 pr-4">{formatNumber(a.decisions)}</td>
                      <td className={`py-2 pr-4 ${KIND_CLASS[a.blockRate.kind] ?? ''}`}>
                        {renderKpi('follow_rate', a.blockRate)}
                      </td>
                      <td className={`py-2 ${KIND_CLASS[a.overrideRate.kind] ?? ''}`}>
                        {renderKpi('follow_rate', a.overrideRate)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </section>
    </div>
  );
}
